/**
 * usePatientStorage.js
 * Persistent + reactive localStorage hook for patient persona
 */

import { useState, useCallback, useEffect } from "react";

const STORAGE_KEY = "patientPersona";

/* ─────────────────────────────────────────────── */
/* Safe read */
/* ─────────────────────────────────────────────── */
export function readPatientPersona() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

/* ─────────────────────────────────────────────── */
/* Safe write (FULL object only) */
/* ─────────────────────────────────────────────── */
function writePatientPersona(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* ignore quota/security errors */
  }
}

/* ─────────────────────────────────────────────── */
/* Clear */
/* ─────────────────────────────────────────────── */
export function clearPatientPersona() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {}
}

/* ─────────────────────────────────────────────── */
/* Hook */
/* ─────────────────────────────────────────────── */
export function usePatientStorage() {
  const [persona, setPersona] = useState(() => readPatientPersona());

  /**
   * Merge updates safely with latest localStorage
   */
  const mergePersona = useCallback((partial = {}) => {
    setPersona(() => {
      const latest = readPatientPersona(); // 🔥 always latest
      const merged = { ...latest, ...partial };

      writePatientPersona(merged);
      return merged;
    });
  }, []);

  /**
   * Replace entire persona (optional use)
   */
  const setFullPersona = useCallback((data = {}) => {
    writePatientPersona(data);
    setPersona(data);
  }, []);

  /**
   * Clear
   */
  const clear = useCallback(() => {
    clearPatientPersona();
    setPersona({});
  }, []);

  /**
   * Sync across tabs / windows
   */
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === STORAGE_KEY) {
        setPersona(readPatientPersona());
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  return [persona, mergePersona, clear, setFullPersona];
}